// 編集中の下書き。**メモリだけに置く**（localStorage・sessionStorage・AsyncStorage に書かない）。
// 共有端末で次の利用者に見えないようにするため。ログアウト時には、この状態をすべて破棄する。
// サーバーの上限（services/futureBoard/validation.mjs）と同じ値で、画面側の確認に使う。
// 最終的な判断は常にサーバーが行う。
import type { CreateFutureCardInput, FutureCard, FutureCardText, UpdateFutureCardInput } from "../../api/futureBoard";

export const LIMITS = {
  // 1人が持てるカードの数の目安（サーバーが最終的に判断する。環境で変わりうるため、超えた応答の limit を優先する）
  cards: 20,
  title: { min: 1, max: 120 },
  meaning: 1000,
  self_image: 500,
  scene: 1500,
  domains: { count: 5, length: 20 },
  sensations: { count: 8, length: 60 },
} as const;

// 領域の候補（仕様 6.1）。表示名は i18n（futureBoard.domains.<キー>）。保存されるのは本人が選んだ語句そのもの。
export const DEFAULT_DOMAIN_KEYS = ["work", "family", "health", "hobby", "learning", "money", "society", "other"] as const;

export interface CardDraft {
  title: string;
  meaning: string;
  self_image: string;
  scene: string;
  domains: string[];
  sensations: string[];
  related_goal_id: number | null;
}

export type DraftField = keyof CardDraft;

export const EMPTY_DRAFT: CardDraft = {
  title: "", meaning: "", self_image: "", scene: "", domains: [], sensations: [], related_goal_id: null,
};

// 文字数は、サーバー（DB の char_length）と同じく、コードポイントで数える。
export const countChars = (value: string): number => [...value].length;

// 前後の空白を削り、空の項目と重複を取り除く（順序は保つ）。
export function normalizeTags(items: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of items) {
    const item = raw.trim();
    if (item && !seen.has(item)) {
      seen.add(item);
      result.push(item);
    }
  }
  return result;
}

const textOrEmpty = (value: string | null | undefined) => value ?? "";

export function draftFromCard(card: Pick<FutureCard, keyof FutureCardText | "related_goal_id">): CardDraft {
  return {
    title: card.title,
    meaning: textOrEmpty(card.meaning),
    self_image: textOrEmpty(card.self_image),
    scene: textOrEmpty(card.scene),
    domains: [...card.domains],
    sensations: [...card.sensations],
    related_goal_id: card.related_goal_id ?? null,
  };
}

// 履歴の過去の版を編集欄に反映する。関連ゴールは履歴に含まれないので、今の値を保つ。
export function draftFromSnapshot(snapshot: FutureCardText, current: CardDraft): CardDraft {
  return { ...draftFromCard({ ...snapshot, related_goal_id: current.related_goal_id }) };
}

export function updateDraft(draft: CardDraft, patch: Partial<CardDraft>): CardDraft {
  return { ...draft, ...patch };
}

const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((item, index) => item === b[index]);

// 保存した内容（base）と比べて、変更された項目。前後の空白だけの違いは変更とみなさない。
export function changedFields(draft: CardDraft, base: CardDraft): DraftField[] {
  const changed: DraftField[] = [];
  for (const field of ["title", "meaning", "self_image", "scene"] as const) {
    if (draft[field].trim() !== base[field].trim()) changed.push(field);
  }
  for (const field of ["domains", "sensations"] as const) {
    if (!sameList(normalizeTags(draft[field]), normalizeTags(base[field]))) changed.push(field);
  }
  if (draft.related_goal_id !== base.related_goal_id) changed.push("related_goal_id");
  return changed;
}

// 未保存の変更があるか（離れるときの「保存・破棄・編集を続ける」の確認に使う）。
export const isDirty = (draft: CardDraft, base: CardDraft): boolean => changedFields(draft, base).length > 0;

const optionalText = (value: string) => (value.trim() ? value.trim() : null);

// 保存する差分（変更した項目だけ）。空にした文章は null（消去）として送る。
export function buildUpdateInput(draft: CardDraft, base: CardDraft, expectedVersion: number): UpdateFutureCardInput | null {
  const fields = changedFields(draft, base);
  if (!fields.length) return null;
  const input: UpdateFutureCardInput = { expected_version: expectedVersion };
  for (const field of fields) {
    if (field === "title") input.title = draft.title.trim();
    else if (field === "meaning" || field === "self_image" || field === "scene") input[field] = optionalText(draft[field]);
    else if (field === "domains" || field === "sensations") input[field] = normalizeTags(draft[field]);
    else input.related_goal_id = draft.related_goal_id;
  }
  return input;
}

// 新規作成の本文。再試行では同じ clientRequestId を使うこと（重複して作られない）。
export function buildCreateInput(draft: CardDraft, clientRequestId: string): CreateFutureCardInput {
  const input: CreateFutureCardInput = { client_request_id: clientRequestId, title: draft.title.trim() };
  const meaning = optionalText(draft.meaning);
  const selfImage = optionalText(draft.self_image);
  const scene = optionalText(draft.scene);
  if (meaning) input.meaning = meaning;
  if (selfImage) input.self_image = selfImage;
  if (scene) input.scene = scene;
  const domains = normalizeTags(draft.domains);
  const sensations = normalizeTags(draft.sensations);
  if (domains.length) input.domains = domains;
  if (sensations.length) input.sensations = sensations;
  if (draft.related_goal_id !== null) input.related_goal_id = draft.related_goal_id;
  return input;
}

export type DraftErrorCode = "required" | "too_long" | "too_many";
export type DraftErrors = Partial<Record<DraftField, DraftErrorCode>>;

export function validateDraft(draft: CardDraft): { ok: boolean; errors: DraftErrors } {
  const errors: DraftErrors = {};
  const titleLength = countChars(draft.title.trim());
  if (titleLength < LIMITS.title.min) errors.title = "required";
  else if (titleLength > LIMITS.title.max) errors.title = "too_long";
  if (countChars(draft.meaning.trim()) > LIMITS.meaning) errors.meaning = "too_long";
  if (countChars(draft.self_image.trim()) > LIMITS.self_image) errors.self_image = "too_long";
  if (countChars(draft.scene.trim()) > LIMITS.scene) errors.scene = "too_long";
  for (const field of ["domains", "sensations"] as const) {
    const items = normalizeTags(draft[field]);
    if (items.length > LIMITS[field].count) errors[field] = "too_many";
    else if (items.some(item => countChars(item) > LIMITS[field].length)) errors[field] = "too_long";
  }
  return { ok: Object.keys(errors).length === 0, errors };
}

// 版の競合（409）のときに、「自分の内容をコピー」で渡す文章。本人の編集内容を失わないために使う。
export function textForClipboard(draft: CardDraft): string {
  const parts = [draft.title, draft.meaning, draft.self_image, draft.scene, draft.domains.join(", "), draft.sensations.join(", ")];
  return parts.map(value => value.trim()).filter(Boolean).join("\n\n");
}
